/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define(['../../DynamicFileLoader', 'underscore','../../lib/@waca/dashboard-common/dist/utils/AutoGroupConverter'], function(DynamicFileLoader, _, AutoGroupConverter) {

	// TODO: look at excluding more features, see AppFactory
	const DEFAULT_FEATURES_EXCLUDES = {
		'com.ibm.bi.dashboard.features': {
			excludes: [
				'ToolbarDock',
				'ToolbarDockDOM',
				'ToolbarPanelDockDOM',
				'OnDemandToolbarSectionDOM',
				'CanvasToolbarSectionDOM',
				'Zoom'
			]
		}
	};

	/**
	*  Live widget service that can be used to provide a live widget functionality
	*  outside of the dashboard environment
	*/
	class LiveWidgetService {
		constructor(options) {
			this._liveWidgetPreviewClass = options && options.liveWidgetPreviewClass;
			//This is used to generate unique lables for auto group at dashboard spec level,
			//ideally should be passed from clients, eg, Smarts.
			this.autoGroupColumnIdsMap = {};
		}

		initialize(glassContext) {
			this.glassContext = glassContext;
			this.logger = this.glassContext.getCoreSvc('.Logger');
			return Promise.resolve();
		}

		_getLiveWidgetPreviewClass() {
			return new Promise((resolve) => {
				if (this._liveWidgetPreviewClass) {
					return resolve(this._liveWidgetPreviewClass);
				} else {
					return DynamicFileLoader.load(['dashboard-analytics/preview/LiveWidgetPreview']).then((modules) => {
						resolve(modules[0]);
					});
				}
			});
		}

		_getEnvironment(options) {
			return this.glassContext.getSvc('.DashboardRuntime').then((svc) => {
				return svc.getRuntimeEnvironment(options);
			});
		}

		/**
		*	Transform a list of recommendations returned by the smarts recommender into an array of live widget specs
		*
		*	@param {object[]} - array of recommendations
		*	@param {object} - model information
		*
		*		{
		*			"assetId": "iD5075523AA154AD091DACC72F97823DC",
		*			"type": "uploadedFile"			// Optional - will save a CM query if available.
		*		}
		*
		*/
		transformRecommendationsToLiveWidgetSpecs(recommendations, modelInfo, dashboardApi) {
			const options = {
				featuresOverride: DEFAULT_FEATURES_EXCLUDES,
				profiles: ['core','consume']
			};
			return this._getEnvironment(options).then((env) => {
				const dataSources = env.dashboardApi.getFeature('dataSources.deprecated');
				return this._getModule(dataSources, modelInfo).then(module => {
					const recommender = env.dashboardApi.getFeature('SmartsRecommender.deprecated');
					return recommender.transformRecommendationsToLiveWidgetSpecs(recommendations, module.getSourceId())
						.then(results => {
							const widgetSpecs = [];
							// Cleanup the internal model ref and replace it with an inline model info
							results.forEach(({ spec }) => {
								if(spec.data && spec.data.dataViews){
									spec.data.dataViews.forEach(dataView => {
										dataView.model = {
											assetId : modelInfo.assetId,
											type: modelInfo.type
										};
										delete dataView.modelRef;
									});
								}
								widgetSpecs.push(spec);
							});
							return this._convertAutoGroup(widgetSpecs,modelInfo,dashboardApi);
						});
				});
			});
		}

		/**
		*	scan and remove auto group columns from cache pool
		*
		*	@param {string} - module reference id
		*	@param {object} - dashboard api
		*	@param {array}	- group column Ids created during the conversion
		*	@returns Promise
		*/
		removeTemporaryGroupColumnsForWidgets (widgets, modelRef, dashboardApi, groupColumnIds) {
			const dataSources = dashboardApi.getFeature('dataSources.deprecated');
			const autoGroupConverter = new AutoGroupConverter({ dataSourcesService: dataSources, groupColumnIds: groupColumnIds });
			return autoGroupConverter.removeTemporaryGroupColumnsForWidgets(widgets, modelRef);
		}

		/**
		*	remove auto group columns in live widget specs
		*
		*	@param {string} - module reference id
		*	@param {object} - dashboard api
		*	@param {array}	- group column ids to be removed
		*	@returns Promise
		*/
		removeTemporaryGroupColumnsForModel ( modelRef, dashboardApi, groupColumnIds) {
			const dataSources = dashboardApi.getFeature('dataSources.deprecated');
			const autoGroupConverter = new AutoGroupConverter({ dataSourcesService: dataSources, groupColumnIds: groupColumnIds });
			return autoGroupConverter.removeTemporaryGroupColumnsForModel(modelRef);
		}

		/**
		*	convert auto group in live widget specs to adopting new api
		*
		*	@param {array}  - array of widgets
		*	@param {string} - module reference id
		*	@param {object} - dashboard api
		*	@param {array}	- group column Ids created during the conversion
		*	@returns Promise with converted widget specs
		*/
		convertAutoGroup(widgets,modelRef, dashboardApi, groupColumnIds, forceSetVisible = false) {
			//TODO. need to come back to fix use one parameter for hideGroupItems and setHidden
			return this._convertInternal(widgets,modelRef, dashboardApi, null, true, groupColumnIds, forceSetVisible);
		}

		_convertAutoGroup(widgets,modelInfo, dashboardApi, forceSetVisible) {
			if ( !dashboardApi ){
				return Promise.resolve(widgets);
			}
			const dataSources = dashboardApi.getFeature('dataSources.deprecated');
			const modelRef = this._getModelRef(dataSources, modelInfo);
			return this._convertInternal(widgets, modelRef, dashboardApi, dataSources, forceSetVisible);
		}

		_convertInternal(widgets, modelRef, dashboardApi, dataSources, hideGroupedItems, groupColumnIds, forceSetVisible ){
			if  (!dataSources) {
				dataSources = dashboardApi.getFeature('dataSources.deprecated');
			}
			const autoGroupConverter = new AutoGroupConverter({ dataSourcesService: dataSources, hideGroupedItems: hideGroupedItems, groupColumnIds: groupColumnIds });
			return autoGroupConverter.convert(widgets, modelRef, this.autoGroupColumnIdsMap, forceSetVisible);
		}

		_getModelRef(dataSources, modelInfo){
			const sourceCollection = dataSources.getSourcesCollection();
			const sourcesId = sourceCollection.getSourceIdsFromAssetId(modelInfo.assetId);
			let modelRef;
			if (sourcesId.length > 0) {
				modelRef = sourcesId[0];
			} else {
				modelRef = sourceCollection.addSource(modelInfo);
			}
			return modelRef;
		}

		_getModule(dataSources, modelInfo) {
			const modelRef = this._getModelRef(dataSources, modelInfo);
			return dataSources.getModule(modelRef).then((module) => {
				return module.ensureTemporaryModule({ forceTemporaryModule: true }).then(() => module);
			});
		}

		renderLiveWidgetPreview(options) {
			return this._getLiveWidgetPreviewClass().then((_LiveWidgetClass) => {
				const optionsWithDefaults = Object.assign({}, { featuresOverride: DEFAULT_FEATURES_EXCLUDES }, options);
				const preview = new _LiveWidgetClass(optionsWithDefaults);
				return preview.render().then(() => {
					return preview;
				});
			});
		}

	}
	return LiveWidgetService;
});
